import { loanApi } from "@api/loans";
import { http } from "@api/base";
import { invenioConfig } from "@config";
import { Loader } from "@components/Loader";
import { ResultsTable } from "@components/ResultsTable/ResultsTable";
import { BackOfficeRoutes } from "@routes/urls";
import { checkBorrowingLimit } from "@utils/CirculationUtils";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Button, Header, Icon, Modal, Message } from "semantic-ui-react";
import { CheckoutModal } from "../../ItemsCheckout/CheckoutModal";
import _isEmpty from "lodash/isEmpty";

export default class ItemsResultsList extends Component {
  state = {
    isModalOpen: false,
    showCheckoutModal: false,
    selectedItem: null,
    borrowingStats: null,
    patronDataEnriched: null,
    loadingStats: false,
  };

  toggleModal = () => {
    const { isModalOpen } = this.state;
    this.setState({ isModalOpen: !isModalOpen });
  };

  enrichPatronData = async (patronPid, itemPid, documentPid) => {
    try {
      // Check for item requests
      const itemLoanRequestsResponse = await loanApi.list(
        loanApi.query().withItemPid(itemPid).withState(["PENDING"]).qs()
      );

      // Check for document requests
      const documentLoanRequestsResponse = await loanApi.list(
        loanApi.query().withDocPid(documentPid).withState(["PENDING"]).qs()
      );

      const patronPidsWithItemRequests = itemLoanRequestsResponse.data.hits.map(
        (loan) => loan.metadata.patron_pid
      );

      const patronPidsWithDocumentRequests =
        documentLoanRequestsResponse.data.hits.map(
          (loan) => loan.metadata.patron_pid
        );

      const hasRequestForThisItem =
        patronPidsWithItemRequests.includes(patronPid);
      const hasRequestForDocument =
        patronPidsWithDocumentRequests.includes(patronPid);

      return {
        hasRequestForThisItem,
        hasRequestForOtherCopy: hasRequestForDocument && !hasRequestForThisItem,
        hasRequest: hasRequestForDocument,
      };
    } catch (error) {
      return {
        hasRequestForThisItem: false,
        hasRequestForOtherCopy: false,
        hasRequest: false,
      };
    }
  };

  openCheckoutModal = async (itemMetadata) => {
    const { patronPid } = this.props;

    this.setState({ loadingStats: true });

    try {
      // Fetch borrowing stats
      const stats = await checkBorrowingLimit(patronPid);

      // Enrich patron data with request information
      const enrichedData = await this.enrichPatronData(
        patronPid,
        itemMetadata.pid,
        itemMetadata.document_pid
      );

      this.setState({
        showCheckoutModal: true,
        selectedItem: itemMetadata,
        borrowingStats: stats,
        patronDataEnriched: enrichedData,
        loadingStats: false,
      });
    } catch (error) {
      this.setState({
        showCheckoutModal: true,
        selectedItem: itemMetadata,
        borrowingStats: null,
        patronDataEnriched: null,
        loadingStats: false,
      });
    }
  };

  closeCheckoutModal = () => {
    this.setState({
      showCheckoutModal: false,
      selectedItem: null,
      borrowingStats: null,
      patronDataEnriched: null,
    });
  };

  // Fetch patron profile data from backend
  fetchPatronProfile = async (patronId) => {
    try {
      const response = await http.get(`/api/patrons/${patronId}/profile`);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  // Fetch program details from backend
  fetchProgramDetails = async (programName) => {
    try {
      const response = await http.get("/courses/programs");
      // Access the nested data array
      const programs = response.data?.data || [];

      if (!Array.isArray(programs)) {
        return "";
      }

      // Find matching program by exact name or partial match
      const program = programs.find(
        (p) => p.name && p.name.toLowerCase() === programName.toLowerCase()
      );

      // If exact match not found, try partial match
      const partialMatch =
        !program &&
        programs.find(
          (p) =>
            p.name && p.name.toLowerCase().includes(programName.toLowerCase())
        );

      const matchedProgram = program || partialMatch;

      return matchedProgram ? matchedProgram.code : "";
    } catch (error) {
      return "";
    }
  };

  handleCheckoutConfirm = async (itemMetadata, force = false) => {
    const { checkoutItem, patronPid } = this.props;

    // Construct the proper item PID structure
    const itemPid = {
      type: "pitmid", // Correct PID type from backend
      value: itemMetadata.pid,
    };

    // Fetch patron profile
    const patronProfile = await this.fetchPatronProfile(patronPid);

    // Extract borrower info
    let borrowerInfo = {
      first_name: "",
      last_name: "",
      contact_no: "",
      college: "",
      program: "",
      program_code: "",
      year: "",
      semester: "",
    };

    if (patronProfile) {
      const fullName = patronProfile.full_name || patronProfile.name || "";
      const nameParts = fullName.trim().split(" ");

      borrowerInfo = {
        first_name: nameParts[0] || "",
        last_name: nameParts.slice(1).join(" ") || "",
        contact_no: patronProfile.contact_number || patronProfile.email || "",
        college: patronProfile.department || "",
        program: patronProfile.program || "",
        program_code: patronProfile.program_code || "",
        year: patronProfile.year || "",
        semester: patronProfile.semester || "",
      };

      // If program_code is missing but we have program name, try to fetch it
      if (!borrowerInfo.program_code && borrowerInfo.program) {
        const programCode = await this.fetchProgramDetails(
          borrowerInfo.program
        );
        if (programCode) {
          borrowerInfo.program_code = programCode;
        }
      }
    }

    // Determine checkout type based on patron enriched data
    const { patronDataEnriched } = this.state;
    let checkoutType = "direct_checkout";
    let requestInfo = null;

    if (patronDataEnriched?.hasRequestForThisItem) {
      checkoutType = "fulfill_specific_request";
      requestInfo = {
        type: "specific_item",
        message: "Fulfilling existing request for this specific copy",
      };
    } else if (patronDataEnriched?.hasRequestForOtherCopy) {
      checkoutType = "fulfill_alternate_request";
      requestInfo = {
        type: "alternate_item",
        message: "Using this copy to fulfill request for different copy",
      };
    } else {
      checkoutType = "direct_checkout";
      requestInfo = {
        type: "library_use",
        message: "Direct checkout for library use - same day return required",
      };
    }

    // Prepare dates
    const today = new Date().toISOString().split("T")[0];

    // Create comprehensive payload
    const payload = {
      force: force,
      borrower_info: borrowerInfo,
      checkoutType: checkoutType,
      requestInfo: requestInfo,
      hasExistingRequest:
        patronDataEnriched?.hasRequestForThisItem ||
        patronDataEnriched?.hasRequestForOtherCopy ||
        false,
      requestPriority: patronDataEnriched?.requestPriority || "normal",
      delivery: { method: "PICKUP" },
      start_date: today,
      end_date: today, // Same day for library use
      request_start_date: today,
      request_expire_date: today,
    };

    checkoutItem(itemMetadata.document_pid, itemPid, patronPid, payload);
    this.closeCheckoutModal();
  };

  actions = ({ row }) => {
    const circulationStatus = !_isEmpty(row.metadata.circulation)
      ? row.metadata.circulation.state
      : null;

    const buttonCheckout = (
      <Button
        primary
        content="Check out"
        onClick={() => {
          this.openCheckoutModal(row.metadata);
        }}
      />
    );

    return !invenioConfig.CIRCULATION.loanActiveStates.includes(
      circulationStatus
    ) && invenioConfig.ITEMS.canCirculateStatuses.includes(row.metadata.status)
      ? buttonCheckout
      : this.renderForceCheckoutModal(row.metadata);
  };

  renderForceCheckoutModal = (itemMetadata) => {
    const { isModalOpen } = this.state;

    return (
      <>
        <Modal
          trigger={
            <Button color="yellow" onClick={this.toggleModal}>
              Force Checkout
            </Button>
          }
          open={isModalOpen}
          onClose={this.toggleModal}
          closeIcon
        >
          <Header
            icon="exclamation triangle"
            content="Force Check out of Item"
          />
          <Modal.Content>
            <p>
              You are creating a loan for a physical copy that cannot circulate.
              Are you sure?
            </p>
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={this.toggleModal}>
              <Icon name="remove" /> No
            </Button>
            <Button
              color="green"
              onClick={() => {
                this.toggleModal();
                this.openCheckoutModal(itemMetadata);
              }}
            >
              <Icon name="checkmark" /> Yes
            </Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  };

  viewDetails = ({ row }) => {
    return (
      <Link
        to={BackOfficeRoutes.itemDetailsFor(row.metadata.pid)}
        data-test={row.metadata.pid}
      >
        {row.metadata.barcode}
      </Link>
    );
  };

  render() {
    const {
      results,
      checkoutIsLoading,
      executedSearch,
      isLoading,
      patronData,
    } = this.props;
    const {
      showCheckoutModal,
      selectedItem,
      borrowingStats,
      loadingStats,
      patronDataEnriched,
    } = this.state;

    const columns = [
      {
        title: "Barcode",
        field: "metadata.barcode",
        formatter: this.viewDetails,
      },
      { title: "Status", field: "metadata.status" },
      { title: "Medium", field: "metadata.medium" },
      { title: "Circulation status", field: "metadata.circulation.state" },
      {
        title: "Actions",
        field: "",
        formatter: this.actions,
      },
    ];

    // Merge patron data with enriched data under metadata
    const mergedPatronData = {
      ...patronData,
      metadata: {
        ...(patronData?.metadata || {}),
        ...patronDataEnriched,
      },
    };

    return (
      <>
        {isLoading && executedSearch ? (
          <Loader isLoading={isLoading} />
        ) : !_isEmpty(results.hits) ? (
          <Loader isLoading={checkoutIsLoading}>
            <p>Found {results.hits.length} item(s).</p>
            <ResultsTable
              data={results.hits}
              columns={columns}
              totalHitsCount={results.length}
              name="items"
            />
          </Loader>
        ) : executedSearch ? (
          <Message
            error
            header="Found no physical copies for this barcode."
            content="Please try again"
          />
        ) : null}

        {showCheckoutModal && selectedItem && (
          <CheckoutModal
            open={showCheckoutModal}
            onClose={this.closeCheckoutModal}
            onCheckout={this.handleCheckoutConfirm}
            item={selectedItem}
            patronData={mergedPatronData}
            borrowingStats={borrowingStats}
            loading={loadingStats}
          />
        )}
      </>
    );
  }
}

ItemsResultsList.propTypes = {
  results: PropTypes.object.isRequired,
  checkoutItem: PropTypes.func.isRequired,
  patronPid: PropTypes.string.isRequired,
  patronData: PropTypes.object,
  checkoutIsLoading: PropTypes.bool,
  executedSearch: PropTypes.bool,
  isLoading: PropTypes.bool,
};

ItemsResultsList.defaultProps = {
  checkoutIsLoading: false,
  isLoading: false,
  executedSearch: false,
  patronData: null,
};
